import axios from 'axios';
import { notification } from 'antd';

import { SERVER_API_ENDPOINT } from 'constants/envs';
import { AUTH_NOTICE_KEY, PUBLIC_AUTH_PATHS } from 'constants/app-info';
import { ROLE_USER } from 'constants/common';
import { API } from 'service/api-route';
import { StorageUtils } from './local-storage';

const AxiosClient = axios.create({
  baseURL: SERVER_API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
});

AxiosClient.defaults.withCredentials = true;

AxiosClient.interceptors.request.use((req) => {
  return req;
});

const hadStoredSession = () => {
  try {
    const raw = localStorage.getItem('SESSION');
    return !!raw && !!JSON.parse(raw)?.accessToken;
  } catch {
    return false;
  }
};

const isOnPublicAuthPath = () => {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return PUBLIC_AUTH_PATHS.includes(path);
};

const redirectToLogin = () => {
  // Đang ở một trang xác thực công khai (vd. sai mật khẩu → 401 ở /login) thì đừng tải lại
  // trang, để thông báo lỗi còn hiện cho người dùng đọc.
  if (isOnPublicAuthPath()) {
    localStorage.removeItem('SESSION');
    return;
  }
  // Có phiên mà bị đá ra → màn Đăng nhập báo "Phiên đăng nhập đã kết thúc…". Nguyên nhân
  // hay gặp: tài khoản vừa đăng nhập ở app/trình duyệt khác (backend xoá phiên cũ).
  if (hadStoredSession()) {
    try {
      sessionStorage.setItem(AUTH_NOTICE_KEY, 'expired');
    } catch {
      // sessionStorage bị chặn: bỏ qua thông báo, vẫn chuyển trang.
    }
  }
  localStorage.removeItem('SESSION');
  window.location.href = '/login';
};

/**
 * Rời vỏ admin vì tài khoản không có ADMIN/MOD (đặc tả §5, lớp 2 — AccessGate trong
 * layouts/layout.tsx, hoặc handleAuthFailure bên dưới nếu AccessGate chưa kịp). Vỏ admin render
 * ngay nên lúc này các request của nó vẫn đang về 401/403: interceptor phải đứng yên, vì tải lại
 * trang về /login sẽ cắt ngang auth/logout (phiên trên server còn sống) và xoá mất notice của
 * điều hướng. Cờ ghi vào sessionStorage để màn Đăng nhập vẫn báo đúng lý do nếu trang bị tải lại.
 */
let notAdminExit = false;

/** Trả false nếu đã có nơi khác đang rời phiên 'notAdmin' (không đăng xuất hai lần). */
export const beginNotAdminExit = (): boolean => {
  if (notAdminExit) return false;
  notAdminExit = true;
  try {
    sessionStorage.setItem(AUTH_NOTICE_KEY, 'notAdmin');
  } catch {
    // sessionStorage bị chặn: AccessGate vẫn gửi notice qua state của điều hướng.
  }
  return true;
};

/** AccessGate gọi khi đã về /login — lỗi đến muộn lúc này rơi vào nhánh trang công khai. */
export const endNotAdminExit = () => {
  notAdminExit = false;
};

/** Phiên còn sống nhưng không có ADMIN/MOD mà AccessGate chưa chặn (vd. auth/status của nó lỗi). */
const exitNotAdmin = async () => {
  if (!beginNotAdminExit()) return;
  try {
    await axios.post(API.logout, null, {
      baseURL: SERVER_API_ENDPOINT,
      withCredentials: true,
      timeout: 8000,
    });
  } catch {
    // Phiên đã hết / mất mạng: phía trình duyệt vẫn xoá phiên như thường.
  }
  try {
    // Như redux/auth/actions.ts#logout: xoá cả tuỳ chọn cục bộ (máy dùng chung).
    localStorage.clear();
  } catch {
    // localStorage bị chặn: bỏ qua.
  }
  window.location.href = '/login';
};

// Cùng cách đọc với getRoleValues/hasAdminAccess (hooks/use-current-roles) — không import hook
// đó vì nó import file này (vòng import).
const hasAdminRole = (res: any) => {
  const user = res?.data?.user || res?.data || {};
  const raw = user?.roles || user?.role;
  const roles: string[] = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
    ? raw.split(',').map((role) => role.trim())
    : [];
  return roles.includes(ROLE_USER.ADMIN) || roles.includes(ROLE_USER.MOD);
};

/**
 * Backend: 403 = phiên đã hết (AuthenticatedGuard) hoặc lỗi quyền riêng (vd. phiếu lương);
 * 401 = phiên còn nhưng không đủ vai trò (RolesGuard). Nên không đá về /login ngay mà hỏi lại
 * `auth/status`. Trước đây MOD bấm nhầm chức năng chỉ ADMIN là bị văng ra màn đăng nhập.
 * Kết quả: null = phiên đã hết; ngược lại là tài khoản có ADMIN/MOD hay không.
 */
type SessionProbe = { admin: boolean } | null;
let sessionProbe: Promise<SessionProbe> | null = null;
const probeSession = () => {
  if (!sessionProbe) {
    sessionProbe = axios
      .get(API.getUserInfo, { baseURL: SERVER_API_ENDPOINT, withCredentials: true })
      .then((res): SessionProbe => ({ admin: hasAdminRole(res) }))
      .catch((): SessionProbe => null)
      .finally(() => {
        setTimeout(() => {
          sessionProbe = null;
        }, 2000);
      });
  }
  return sessionProbe;
};

let lastForbiddenNotice = 0;
const notifyForbidden = () => {
  // Một trang có thể bắn vài request cùng lúc — chỉ báo một lần.
  if (Date.now() - lastForbiddenNotice < 3000) return;
  lastForbiddenNotice = Date.now();
  notification.warning({
    message: 'Không đủ quyền',
    description: 'Tài khoản của bạn không được phép thực hiện thao tác này.',
  });
};

const handleAuthFailure = async (status: number, url: string) => {
  // Đang rời phiên 'notAdmin': để AccessGate / exitNotAdmin tự điều hướng.
  if (notAdminExit) return;
  const onPublicPage = isOnPublicAuthPath();
  // 401 ở trang công khai (sai mật khẩu, tài khoản bị khoá...): chưa có phiên để hỏi lại.
  if (status === 401 && onPublicPage) {
    redirectToLogin();
    return;
  }
  const probe = url.includes(API.getUserInfo) ? null : await probeSession();
  // Trong lúc chờ đã rời vỏ admin (Đăng xuất, AccessGate): lỗi của trang cũ, bỏ qua.
  if (notAdminExit || (!onPublicPage && isOnPublicAuthPath())) return;
  if (!probe) {
    redirectToLogin();
  } else if (!probe.admin && !onPublicPage) {
    await exitNotAdmin();
  } else {
    notifyForbidden();
  }
};

AxiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: any) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      await handleAuthFailure(status, error?.config?.url || '');
    }
    // Không có phản hồi (mất mạng, CORS, timeout): gắn networkError để trang xác thực phân biệt
    // với lỗi máy chủ (layouts/auth/auth-errors.ts). Nơi khác chỉ đọc `.message` nên không đổi.
    throw error?.response?.data ?? { message: 'Không kết nối được máy chủ.', networkError: true };
  }
);

/**
 * Ép kiểu kết quả trả về của axios sang kiểu mà nơi gọi khai báo.
 *
 * Vì sao cần: interceptor phía trên trả về NGUYÊN đối tượng response của axios
 * chứ không phải response.data, và các nơi gọi đang dựa vào điều đó — ví dụ
 * redux/auth/actions.ts đọc `res.status` rồi mới tới `res.data`. Nên
 * `Promise<ResType>` ở các hàm dưới vốn đã không phản ánh đúng hình dạng dữ
 * liệu lúc chạy; đây là quy ước có sẵn của dự án, không phải thứ tôi tạo ra.
 *
 * Trước đây TypeScript vẫn cho qua vì axios cũ khai báo `R = AxiosResponse<T>`
 * nên TS suy ra được `R = ResType` từ kiểu trả về của hàm bao ngoài. Từ axios
 * 1.20 kiểu trả về đổi thành conditional type (`AxiosResponseResult`), TS không
 * suy ra được nữa nên báo lỗi ở cả 6 hàm.
 *
 * Ép kiểu tường minh ở một chỗ duy nhất giữ nguyên hành vi lúc chạy và không
 * vỡ lại khi nâng cấp axios sau này. Muốn sửa tận gốc thì phải đổi kiểu trả về
 * thành `Promise<AxiosResponse<ResType>>` và cập nhật toàn bộ nơi gọi — việc đó
 * nên làm riêng.
 */
const asResponse = <ResType>(promise: Promise<unknown>): Promise<ResType> =>
  promise as Promise<ResType>;

const get = <ReqType, ResType = any>(
  url: string,
  params?: ReqType,
  customHeaders?: any,
  extraConfig?: any
): Promise<ResType> => {
  return asResponse<ResType>(
    AxiosClient.get(url, { params, headers: customHeaders, ...extraConfig })
  );
};

function post<ReqType, ResType>(
  url: string,
  data?: ReqType,
  customHeaders?: any
): Promise<ResType> {
  return asResponse<ResType>(AxiosClient.post(url, data, { headers: customHeaders }));
}

function postS3<ReqType, ResType>(
  url: string,
  data?: ReqType,
  customHeaders?: any
): Promise<ResType> {
  return asResponse<ResType>(AxiosClient.post(url, data, { headers: customHeaders }));
}

async function put<ReqType, ResType>(
  url: string,
  data?: ReqType,
  customHeaders?: any
): Promise<ResType> {
  return asResponse<ResType>(AxiosClient.put(url, data, { headers: customHeaders }));
}

async function patch<ReqType, ResType>(
  url: string,
  data?: ReqType,
  customHeaders?: any
): Promise<ResType> {
  return asResponse<ResType>(AxiosClient.patch(url, data, { headers: customHeaders }));
}

async function _delete<ReqType, ResType>(url: string, payload: ReqType): Promise<ResType> {
  return asResponse<ResType>(AxiosClient.delete(url, { data: payload }));
}

const AxiosUtils = { get, post, postS3, put, patch, _delete };
export default AxiosUtils;
